const formatServiceDate = (dateString) => {
  if (!dateString) return 'Zakazano';
  try {
    return new Date(dateString).toLocaleDateString('sr-RS', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return 'Zakazano';
  }
};

/** Partner kontakt iz assignedTo (partner:<id>). */
export const getPartnerFromAssignedTo = (assignedTo, partners = []) => {
  if (!assignedTo || assignedTo === 'Unassigned') return null;
  const value = String(assignedTo).trim();
  if (!value.startsWith('partner:')) return null;
  const partnerId = value.replace('partner:', '').trim();
  if (!partnerId) return null;
  return (
    (partners || []).find((p) => String(p._id || p.id || '') === partnerId) || null
  );
};

/** Da li dodela servisnom partneru pokreće prosleđivanje (registrovana SpinTasker firma). */
export const isLinkedServicePartnerAssignment = (assignedTo, partners = []) => {
  const partner = getPartnerFromAssignedTo(assignedTo, partners);
  return !!(partner?.linkedCompanyId);
};

/** Da li je posao ikada prosleđen od strane trenutne kompanije (bez ikakvih drugih filtera). */
export const wasJobForwardedByCompany = (job, currentCompanyId) => {
  if (!job || !currentCompanyId) return false;
  const cid = String(currentCompanyId);

  if (
    job.handoffStatus === 'pending_acceptance' &&
    job.assignedPartnerCompanyId &&
    job.companyId &&
    String(job.companyId) === cid
  ) {
    return true;
  }

  if (job.sourceCompanyId && String(job.sourceCompanyId) === cid) {
    return true;
  }

  return false;
};

export const resolveHandoffWorkflowPhase = (job, currentCompanyId = null) => {
  if (!job) return null;
  if (job.handoffDeletedByPartner) return 'deleted_by_partner';
  if (job.handoffPartnerClosed || job.handoffWorkflowPhase === 'closed') return 'closed';

  const phase = job.handoffWorkflowPhase;
  if (phase && phase !== 'closed') {
    if (job.status === 'Finished' || job.status === 'Completed') return 'ready_to_close';
    if (job.serviceDate && (phase === 'accepted' || phase === 'forwarded')) return 'scheduled';
    return phase;
  }

  const cid = currentCompanyId ? String(currentCompanyId) : null;

  if (
    cid &&
    job.handoffStatus === 'pending_acceptance' &&
    job.assignedPartnerCompanyId &&
    String(job.assignedPartnerCompanyId) === cid
  ) {
    return 'forwarded';
  }

  if (
    cid &&
    job.handoffStatus === 'pending_acceptance' &&
    job.companyId &&
    String(job.companyId) === cid
  ) {
    return 'forwarded';
  }

  if (job.sourceCompanyId) {
    if (job.status === 'Finished' || job.status === 'Completed') return 'ready_to_close';
    if (job.serviceDate) return 'scheduled';
    return 'accepted';
  }

  return null;
};

/** Posao primljen od centralnog servisa — servisni partner ga vidi u nezakazanim poslovima. */
export const isPartnerHandoffPipelineJob = (job, currentCompanyId) => {
  if (!job || !currentCompanyId || job.handoffPartnerClosed || job.handoffDeletedByPartner) return false;
  const cid = String(currentCompanyId);

  // Posao primljen od drugog centralnog servisa (mi smo primalac, ne pošiljalac)
  if (
    job.sourceCompanyId &&
    job.companyId &&
    String(job.companyId) === cid &&
    String(job.sourceCompanyId) !== cid
  ) {
    return true;
  }

  if (
    job.handoffStatus === 'pending_acceptance' &&
    job.assignedPartnerCompanyId &&
    String(job.assignedPartnerCompanyId) === cid
  ) {
    return true;
  }

  return false;
};

/** Posao je prosleđen od strane trenutne kompanije (pošiljalac gleda status kod partnera). */
export const isOutgoingHandoffSourceView = (job, currentCompanyId) => {
  if (!job || !currentCompanyId || !job.sourceCompanyId) return false;
  return String(job.sourceCompanyId) === String(currentCompanyId);
};

export const isOutgoingHandoffJob = wasJobForwardedByCompany;

/** Da li prosleđeni posao još treba da bude u listi pošiljaoca (do centralnog arhiviranja). */
export const isOutgoingHandoffActiveForSender = (job, currentCompanyId) =>
  isOutgoingHandoffJob(job, currentCompanyId) && !job?.handoffSenderArchived;

/**
 * Zaključaj radnika/partnera samo kod kompanije koja je poslala prosleđivanje
 * (dok primalac ne prihvati ili odbije). Ne zaključavaj samo zato što posao ima sourceCompanyId
 * — to važi i za partnere koji su primili posao od centralnog servisa.
 */
export const isOutgoingHandoffAssignmentLocked = (job, currentCompanyId) => {
  if (!job || !currentCompanyId || job.handoffDeletedByPartner) return false;
  if (job.handoffStatus === 'returned' || job.handoffStatus === 'none') return false;

  const cid = String(currentCompanyId);

  return (
    job.handoffStatus === 'pending_acceptance' &&
    job.assignedPartnerCompanyId &&
    job.companyId &&
    String(job.companyId) === cid
  );
};

const getJobCompletedDate = (job) =>
  job?.report?.completedAt || job?.report?.submittedAt || job?.updatedAt || null;

export const getHandoffWorkflowBadge = (job, currentCompanyId = null) => {
  const phase = resolveHandoffWorkflowPhase(job, currentCompanyId);
  if (!phase) return null;

  const isSenderView = isOutgoingHandoffSourceView(job, currentCompanyId);

  switch (phase) {
    case 'deleted_by_partner': {
      const deletedLabel = formatServiceDate(job.handoffDeletedByPartnerAt);
      return {
        className: 'bg-gray-600',
        label: deletedLabel === 'Zakazano' ? 'Obrisan od partnera' : `Obrisan ${deletedLabel}`,
        isAction: false
      };
    }
    case 'closed':
      if (isSenderView) {
        return { className: 'bg-slate-600', label: 'Zatvoreno', isAction: false };
      }
      return null;
    case 'forwarded':
      if (job.handoffIsReturn) {
        return { className: 'bg-rose-600', label: 'Posao je vraćen', isAction: false };
      }
      return { className: 'bg-red-600', label: 'Prosleđeno', isAction: false };
    case 'accepted':
      return { className: 'bg-orange-500', label: 'Prihvaćeno', isAction: false };
    case 'scheduled': {
      const dateLabel = formatServiceDate(job.serviceDate);
      return { className: 'bg-blue-600', label: dateLabel, isAction: false };
    }
    case 'new_visit':
      return { className: 'bg-purple-600', label: 'Nova poseta', isAction: false };
    case 'ready_to_close':
      if (isSenderView) {
        const completedLabel = formatServiceDate(getJobCompletedDate(job));
        return {
          className: 'bg-green-600',
          label: completedLabel === 'Zakazano' ? 'Završeno' : `Završeno ${completedLabel}`,
          isAction: false,
          title: 'Otvorite detalje posla za obračun provizije i troškova'
        };
      }
      return { className: 'bg-green-600', label: 'Završi stranku', isAction: true };
    default:
      return null;
  }
};

export const isSubcontractPartnerJob = (job) =>
  !!(job?.sourceCompanyId && !job?.handoffPartnerClosed && !job?.handoffDeletedByPartner);

export const canShowPartnerArchiveControls = (job) =>
  !!(job?.sourceCompanyId && job?.handoffPartnerClosed && job?.status === 'Finished');

export const sortOutgoingHandoffJobs = (jobs = []) =>
  [...jobs].sort((a, b) => {
    const da = new Date(a.handoffForwardedAt || a.handoffAcceptedAt || a.createdAt || 0).getTime();
    const db = new Date(b.handoffForwardedAt || b.handoffAcceptedAt || b.createdAt || 0).getTime();
    return db - da;
  });

/** Aktivni prosleđeni poslovi za jednog partnera (pogled pošiljaoca). */
export const getPartnerOutgoingHandoffJobs = (partner, jobs = [], currentCompanyId) =>
  (jobs || []).filter((job) =>
    isOutgoingHandoffJobForPartner(job, partner, currentCompanyId)
  );

/** Broj prosleđenih i zatvorenih poslova kod partnera — običan tekst u listi partnera. */
export const getPartnerHandoffSummary = (partner, jobs = [], currentCompanyId) => {
  const partnerJobs = getPartnerOutgoingHandoffJobs(partner, jobs, currentCompanyId);

  const closedByPartnerCount = partnerJobs.filter((job) => {
    if (job.handoffPartnerClosed) return true;
    const phase = resolveHandoffWorkflowPhase(job, currentCompanyId);
    if (phase === 'closed' || phase === 'ready_to_close') return true;
    return job.status === 'Finished' || job.status === 'Completed';
  }).length;

  return {
    forwardedCount: partnerJobs.length,
    closedByPartnerCount
  };
};

/** Da li je aktivan prosleđeni posao vezan za datog partnera (pogled pošiljaoca). */
export const isOutgoingHandoffJobForPartner = (job, partner, currentCompanyId) => {
  if (!job || !partner || !currentCompanyId) return false;
  if (!isOutgoingHandoffActiveForSender(job, currentCompanyId)) return false;

  const partnerId = String(partner._id || partner.id || '');
  const linkedId = partner.linkedCompanyId ? String(partner.linkedCompanyId) : null;
  const cid = String(currentCompanyId);

  const assigned = String(job.assignedTo || '');
  if (assigned === `partner:${partnerId}` || assigned === partnerId) return true;

  if (linkedId) {
    if (
      job.handoffStatus === 'pending_acceptance' &&
      String(job.companyId) === cid &&
      String(job.assignedPartnerCompanyId || '') === linkedId
    ) {
      return true;
    }

    if (
      job.sourceCompanyId &&
      String(job.sourceCompanyId) === cid &&
      String(job.companyId || '') === linkedId
    ) {
      return true;
    }
  }

  return false;
};
